import {
  ActionIcon,
  Button,
  ColorSwatch,
  Group,
  Image,
  Loader,
  SimpleGrid,
  Text,
  Tooltip,
} from "@mantine/core";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Pencil, Plus, Tag, Trash2 } from "lucide-react";
import { useState } from "react";

import { useListBrands } from "../../api/generated/endpoints/teams/teams";
import { freshUrls, soonestExpiry } from "../../api/signed-urls";
import type { Brand } from "../../api/generated/model";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { useMembership } from "../../platform/membership";
import { managesBrands } from "../../platform/permissions";
import { useNavBrands } from "../../platform/shell/nav-links";
import { ContentCard } from "../../ui/ContentCard";
import { EmptyState } from "../../ui/EmptyState";
import { PageColumns } from "../../ui/PageColumns";
import { SearchToolbar } from "../../ui/SearchToolbar";
import { DeleteBrand } from "../../platform/brands/DeleteBrand";

const ROUTE = "/signed-in/teams/$teamId/brands";

/**
 * The team's brands as the live app shows them (design-system.md 3.6): a search that filters by
 * name as you type (kept in `?search=`), "Add Brand" for admins, and a card per brand with its
 * logo, website and colors, which opens the brand's Library as live's does (spec A3d); an admin
 * edits or deletes one from its card. The brand the header's select shows has the primary border.
 */
export function BrandListScreen() {
  const { teamId } = useParams({ from: ROUTE });
  const search = useSearch({ from: ROUTE });
  const navigate = useNavigate({ from: "/teams/$teamId/brands" });
  const me = useMembership(teamId);
  // The logos' signed URLs expire: the list is read again before they do (#123).
  const brands = useListBrands(teamId, {
    query: freshUrls((list: Brand[]) => soonestExpiry(list.map((brand) => brand.urls_expire_at))),
  });
  const { current } = useNavBrands(teamId);
  const [deleting, setDeleting] = useState<Brand | undefined>(undefined);
  const canManage = me !== undefined && managesBrands(me.role);
  const query = (search.search ?? "").toLowerCase();
  const shown = (brands.data ?? []).filter((brand) => brand.name.toLowerCase().includes(query));
  const toolbar = (
    <SearchToolbar
      search={{
        label: "Search",
        value: search.search ?? "",
        // The list is already read: the cards follow each key, as live.
        delay: 0,
        onChange: (value) =>
          void navigate({ search: { search: value || undefined }, replace: true }),
      }}
      right={
        canManage ? (
          <Button
            variant="outline"
            leftSection={<Plus size={20} />}
            renderRoot={(props) => (
              <Link {...props} to="/teams/$teamId/brands/new" params={{ teamId }} />
            )}
          >
            Add Brand
          </Button>
        ) : null
      }
    />
  );
  return (
    <PageColumns top={toolbar}>
      {brands.isPending ? <Loader size="sm" aria-label="Loading brands" /> : null}
      {brands.isError ? <ErrorAlert error={brands.error} /> : null}
      {brands.isSuccess && shown.length === 0 ? (
        <EmptyState
          image={<Tag size={48} strokeWidth={1.5} />}
          title={query === "" ? "No brands yet" : "No brands found"}
          {...(query === "" ? {} : { description: "Try a different search query." })}
        />
      ) : null}
      {/* Live insets the cards about 16px from the toolbar's edges (screenshot 08). */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4, xl: 5 }} spacing="xl" px={17} py={1}>
        {shown.map((brand) => (
          <BrandCard
            key={brand.id}
            teamId={teamId}
            brand={brand}
            active={brand.id === current?.id}
            onDelete={canManage ? () => setDeleting(brand) : undefined}
          />
        ))}
      </SimpleGrid>
      {deleting === undefined ? null : (
        <DeleteBrand teamId={teamId} brand={deleting} onCancel={() => setDeleting(undefined)} />
      )}
    </PageColumns>
  );
}

interface BrandCardProps {
  teamId: string;
  brand: Brand;
  /** The brand the header's select shows. */
  active: boolean;
  /** Present for those who may delete it; editing follows the same rule. */
  onDelete: (() => void) | undefined;
}

function BrandCard({ teamId, brand, active, onDelete }: BrandCardProps) {
  const params = { teamId, brandId: brand.id };
  const actions =
    onDelete === undefined ? undefined : (
      <Group gap={0}>
        <Tooltip label="Edit">
          <ActionIcon
            variant="text-hover"
            size={32}
            aria-label={`Edit ${brand.name}`}
            renderRoot={(props) => (
              <Link {...props} to="/teams/$teamId/brands/$brandId/edit" params={params} />
            )}
          >
            <Pencil size={16} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Delete">
          <ActionIcon
            variant="text-hover"
            size={32}
            aria-label={`Delete ${brand.name}`}
            onClick={onDelete}
          >
            <Trash2 size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>
    );
  return (
    <ContentCard
      media={<BrandLogo name={brand.name} url={brand.logo_url} />}
      title={brand.name}
      active={active}
      {...(brand.website === null ? {} : { description: brand.website })}
      // Live selects the brand and opens its Library: the brand is the URL's, so the link does both.
      link={(props) => (
        <Link {...props} to="/teams/$teamId/brands/$brandId/library" params={params} />
      )}
      footer={
        <Group gap={4} aria-hidden>
          {brand.colors.map((color) => (
            <ColorSwatch key={color.hex} color={color.hex} size={12} radius="xs" />
          ))}
        </Group>
      }
      actions={actions}
    />
  );
}

/**
 * A brand's logo from its signed URL (#123), or its initial at 48px when it has none or the URL
 * no longer loads.
 */
function BrandLogo({ name, url }: { name: string; url: string | null }) {
  const [failed, setFailed] = useState<string | null>(null);
  if (url === null || url === failed) {
    return (
      <Text aria-hidden fz={48} fw={700} c="var(--cai-color-text-muted)">
        {name.trim().charAt(0).toUpperCase()}
      </Text>
    );
  }
  return <Image src={url} alt="" h="100%" fit="contain" onError={() => setFailed(url)} />;
}
